import { Injectable } from '@angular/core';
import * as Excel from '@grapecity/spread-excelio';
import * as GC from '@grapecity/spread-sheets';
import { flat } from '@helpers/arrays';
import { sleep } from '@helpers/sleep';
import * as spreadCellStyling from '@helpers/spread/cell-styling';
import { DisplayZeroAsMappingItem } from '@models/results/display-zero-as-mapping-item';
import { EllipsisMappingItem } from '@models/results/ellipsis-mapping-item';
import { ResultsetItem } from '@models/resultset-item';
import { TableDataService } from '@services/lookup-tables/table-data.service';
import { ApplicationStatus } from '@services/results/application-status';
import { HIGHLIGHTS_TAB, ResultTabsService } from '@services/results/result-tabs.service';
import { ResultsStoreService } from '@services/results/results-store.service';
import { ResultsService } from '@services/results/results.service';
import { spreadsheetConfig } from '@shared/spreadsheet/spreadsheet-config';
import * as FileSaver from 'file-saver';
import { forkJoin } from 'rxjs';

const MAX_TAB_LEN = 31; // Maximum tab name length in Excel

const SUMMARY_TAB = 'Summary';
const LOOKUP_TAB = 'Lookup Tables';
const FIXED_TABS = [SUMMARY_TAB, HIGHLIGHTS_TAB, LOOKUP_TAB];
const DETAIL_INDICATOR = '🔎';
const SUMMARY_INDICATOR = '➕';
const TAB_COLOR_FIXED = 'Accent 1';

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  constructor(
    private readonly tableDataService: TableDataService,
    private readonly resultsService: ResultsService,
    private readonly resultsStore: ResultsStoreService,
    private readonly tabsService: ResultTabsService
  ) {}

  public async exportSheets(exportLookupTables = true, convertEllipsisOnExport = true, convertDisplayZeroAsOnExport = false) {
    const spread: GC.Spread.Sheets.Workbook = new GC.Spread.Sheets.Workbook();
    const tempSpread: GC.Spread.Sheets.Workbook = new GC.Spread.Sheets.Workbook();

    spread.suspendPaint();
    spread.suspendEvent();

    if (spread && spread.getSheetIndex('Sheet1') > -1) {
      spread.removeSheet(spread.getSheetIndex('Sheet1'));
    }

    // Add Lookup Tables tab
    if (exportLookupTables) {
      await this.exportLookupTables();
    }

    const missingResults = await this.pullMissingResults();

    if (missingResults?.length > 0) {
      while (
        this.resultsStore.appStatus$.getValue() === ApplicationStatus.BuildingSummary ||
        this.resultsStore.appStatus$.getValue() === ApplicationStatus.FetchSingleResult
      ) {
        await sleep(100);
      }
    }

    // Clone the object to don't affect the originals
    const spreadJSON = this.resultsService.spread.toJSON();
    tempSpread.fromJSON(spreadJSON);

    const tabTree = this.tabsService.tabCollection$.getValue();
    const flattenedTabTree = tabTree.reduce((prev, curr) => {
      if (curr.StressList) {
        return prev.concat(curr).concat(curr.StressList);
      }
      return prev.concat(curr);
    }, []);

    spread.addSheet(0, tempSpread.getSheetFromName(SUMMARY_TAB));
    spread.addSheet(1, tempSpread.getSheetFromName('Highlights'));

    const summarySheets: GC.Spread.Sheets.Worksheet[] = [];
    const detailSheets: GC.Spread.Sheets.Worksheet[] = [];

    try {
      for (let i = 0; i < flattenedTabTree.length; i++) {
        const tab = flattenedTabTree[i];
        const sheet = tempSpread.getSheetFromName(tab.Mnemonic);
        if (sheet) {
          const sheetCopyJSON = sheet.toJSON();
          const sheetCopy: GC.Spread.Sheets.Worksheet = new GC.Spread.Sheets.Worksheet(tab.Mnemonic);
          sheetCopy.fromJSON(sheetCopyJSON);
          detailSheets.push(sheetCopy);
        } else {
          console.error(`Couldnt find sheet for mnemonic ${tab.Mnemonic}`);
        }

        const summarySheet = tempSpread.getSheetFromName(`${tab.Mnemonic}-summary`);
        if (summarySheet) {
          const sheetCopyJSON = summarySheet.toJSON();
          const sheetCopy: GC.Spread.Sheets.Worksheet = new GC.Spread.Sheets.Worksheet(`${tab.Mnemonic}-summary`);
          sheetCopy.fromJSON(sheetCopyJSON);
          summarySheets.push(sheetCopy);
        } else {
          console.error(`Couldnt find sheet for mnemonic ${tab.Mnemonic}-summary`);
        }
      }
    } catch (error) {
      console.warn(error);
    }

    spread.options.tabStripVisible = true;

    for (let i = summarySheets.length - 1; i >= 0; i--) {
      const summarySheet = summarySheets[i];

      try {
        // Move 'Summary' tabs before 'Detail' ones (2/2)
        spread.addSheet(FIXED_TABS.length - 1, summarySheet);
      } catch (error) {
        console.warn(error);
      }
    }

    for (let i = detailSheets.length - 1; i >= 0; i--) {
      const detailSheet = detailSheets[i];

      try {
        // Move 'Summary' tabs before 'Detail' ones (2/2)
        spread.addSheet(FIXED_TABS.length - 1 + summarySheets.length, detailSheet);
      } catch (error) {
        console.warn(error);
      }
    }

    this.removeHiddenTabs(spread);

    // Shorten tab names
    try {
      const tabNames = [];

      for (let i = 0; i < spread.getSheetCount(); i++) {
        const sheet: GC.Spread.Sheets.Worksheet = spread.getSheet(i);
        let sheetName: string = sheet.name();

        // Add (D) to detail tabs and (S) to summary tabs
        if (FIXED_TABS.includes(sheetName)) {
          sheet.options.sheetTabColor = TAB_COLOR_FIXED;
        } else {
          sheetName = sheetName.includes('-summary')
            ? `${SUMMARY_INDICATOR} ${sheetName.replace('-summary', '')}`
            : `${DETAIL_INDICATOR} ${sheetName}`;
        }

        let finalTabName = sheetName.length > MAX_TAB_LEN ? `${sheetName.slice(0, MAX_TAB_LEN - 3)}...` : sheetName;

        const count = tabNames.filter(t => t === finalTabName).length;
        if (count === 0) {
          tabNames.push(finalTabName);
        } else {
          finalTabName = `${sheetName.slice(0, MAX_TAB_LEN - (5 + count.toString().length))}...(${count})`;
        }

        tabNames.push(finalTabName);
        sheet.name(finalTabName);
      }
    } catch (error) {
      console.warn({ error });
    }

    spread.addSheet(spread.getSheetCount(), tempSpread.getSheetFromName(LOOKUP_TAB));

    // CONVERSIONS
    // ===========
    if (convertEllipsisOnExport && this.resultsStore && this.resultsStore.ellipsisMapping) {
      this.convertEllipsis(spread);
      // console.log('Ellipsis Conversion done');
    }

    if (convertDisplayZeroAsOnExport && this.resultsStore && this.resultsStore.displayZeroAsMapping) {
      this.convertDisplayZeroAs(spread);
    }

    // Auto fit columns for Lookup Tables
    const lut_sheet: GC.Spread.Sheets.Worksheet = spread.getSheet(spread.getSheetIndex(LOOKUP_TAB));
    if (lut_sheet) {
      const cols = lut_sheet.getColumnCount();
      for (let col = 0; col < cols; col++) {
        spreadCellStyling.autoFitColumn(lut_sheet, col);
      }
    }

    // Auto fit rows height for 'Summary' tab
    const summary_sheet: GC.Spread.Sheets.Worksheet = spread.getSheet(spread.getSheetIndex(SUMMARY_TAB));
    if (summary_sheet) {
      summary_sheet.setColumnWidth(0, 1800, GC.Spread.Sheets.SheetArea.viewport);
      spreadCellStyling.autoFitColumn(summary_sheet, 0);
      for (let r = 0; r < summary_sheet.getRowCount(); r++) {
        const cell = summary_sheet.getCell(r, 0, GC.Spread.Sheets.SheetArea.viewport);
        const value = cell.value();
        cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);
        cell.vAlign(GC.Spread.Sheets.VerticalAlign.center);
        cell.textIndent(2);
        cell.value(value);
        summary_sheet.autoFitRow(r);
      }
    }

    // Selected sheet
    spread.setActiveSheet(HIGHLIGHTS_TAB);

    for (let i = 0; i < spread.getSheetCount(); i++) {
      const sheet: GC.Spread.Sheets.Worksheet = spread.getSheet(i);

      if (sheet) {
        sheet.options.isProtected = false;
        sheet.comments.clear(null);
      }
    }

    // Convert it to JSON for export
    const json = spread.toJSON();

    // As Of Date
    const monthVal = this.resultsService.asOfDate.getMonth() + 1; //defect 5007

    // If any of them is 1-digit number, add a 0 at the start
    const asOfDate = [
      `${monthVal}`.length === 1 ? `0${monthVal}` : monthVal,
      `${this.resultsService.asOfDate.getDate()}`.length === 1
        ? `0${this.resultsService.asOfDate.getDate()}`
        : this.resultsService.asOfDate.getDate(),
      `${this.resultsService.asOfDate.getFullYear()}`.length === 1
        ? `0${this.resultsService.asOfDate.getFullYear()}`
        : this.resultsService.asOfDate.getFullYear(),
    ].join('.');

    // File name
    const fileName = `LST_Report_${asOfDate}.xlsx`;

    const excelIO = new Excel.IO();
    excelIO.save(
      json,
      (blob: string | Blob) => {
        FileSaver.saveAs(blob, fileName);
      },
      (err: any) => {
        console.warn(`Error saving file "${fileName}"`, err);
      }
    );

    spread.destroy();
    console.log('Export done');
  }

  private async pullMissingResults() {
    const missingResults = this.resultsStore.resultsList
      .filter(result => !result?.InMemory)
      .map(result => this.resultsStore.getSingleResultset(result.RunID));
    return forkJoin(missingResults).toPromise();
  }

  private async exportLookupTables() {
    const toExport = true;
    const lookupTablesSheet = await this.tableDataService.getLookupTablesSheet(toExport);
    const numberOfSheets = this.resultsService.spread.getSheetCount();

    if (this.resultsService.spread.getSheetFromName(LOOKUP_TAB) !== null) {
      this.resultsService.spread.removeSheet(this.resultsService.spread.getSheetIndex(LOOKUP_TAB));
    }
    this.resultsService.spread.addSheet(numberOfSheets + 1, lookupTablesSheet);
  }

  private convertEllipsis(spread: GC.Spread.Sheets.Workbook) {
    const suspendedSheets = [];
    this.resultsStore.ellipsisMapping.forEach((mappingItem: EllipsisMappingItem, mappingKey) => {
      const sheet: GC.Spread.Sheets.Worksheet = spread.getSheetFromName(mappingItem.sheet.name());
      if (sheet) {
        if (!sheet.isPaintSuspended()) {
          sheet.suspendPaint();
          suspendedSheets.push(sheet);
        }
        const cell: GC.Spread.Sheets.CellRange = sheet.getCell(mappingItem.row, mappingItem.col);
        cell.value(mappingItem.numericValue);
      }
    });
    suspendedSheets.forEach(sheet => sheet.resumePaint());
  }

  private convertDisplayZeroAs(spread: GC.Spread.Sheets.Workbook) {
    const suspendedSheets = [];
    this.resultsStore.displayZeroAsMapping.forEach((mappingItem: DisplayZeroAsMappingItem, mappingKey) => {
      const sheet: GC.Spread.Sheets.Worksheet = spread.getSheetFromName(mappingItem.sheet.name());
      if (!sheet.isPaintSuspended()) {
        sheet.suspendPaint();
        suspendedSheets.push(sheet);
      }
      let displayZeroAs: string;
      let displayNegativeAs: string;
      let displayPositiveAs: string;
      let displayDefaultAs: string;
      const breakFormats = mappingItem.format.split(';');
      switch (breakFormats.length) {
        case 4:
          displayDefaultAs = breakFormats[3];
        /* falls through */
        case 3:
          displayZeroAs = breakFormats[2]
            // LOOK FOR displayZeroAs 0 and change It to "-"
            .replace('0', spreadsheetConfig.displayZeroAs); // Changes 0 for "-"

        /* falls through */
        case 2:
          displayNegativeAs = breakFormats[1];
        /* falls through */
        case 1:
          displayPositiveAs = breakFormats[0];
          break;
      }
      const format = [displayPositiveAs, displayNegativeAs, displayZeroAs, displayDefaultAs].join(';');
      const cell: GC.Spread.Sheets.CellRange = sheet.getCell(mappingItem.row, mappingItem.col);
      cell.formatter(format);
    });
    suspendedSheets.forEach(sheet => sheet.resumePaint());
    // console.log('displayZeroAs Conversion done');
  }

  public removeHiddenTabs(spread: GC.Spread.Sheets.Workbook) {
    spread.suspendEvent();
    spread.suspendCalcService(true);
    spread.suspendPaint();

    const resultTabs: ResultsetItem[] = this.tabsService.tabCollection$.value;

    let baselineTabsToShow = resultTabs.filter(t => !t.Hidden).map(x => x.Mnemonic);
    baselineTabsToShow = [...baselineTabsToShow, ...baselineTabsToShow.map(x => x + '-summary')];
    baselineTabsToShow = [...FIXED_TABS, ...baselineTabsToShow];

    let stressTabsToShow = flat(resultTabs.map(s => s.StressList))
      .filter((s: { Hidden: any }) => !s.Hidden)
      .map((s: { Mnemonic: any }) => s.Mnemonic);
    stressTabsToShow = [...stressTabsToShow, ...stressTabsToShow.map((x: string) => x + '-summary')];

    const tabsToShow = [...baselineTabsToShow, ...stressTabsToShow];
    const tabsToHide = [];

    for (let i = 0; i < spread.getSheetCount(); i++) {
      const sheet: GC.Spread.Sheets.Worksheet = spread.getSheet(i);
      const tabName = sheet.name();
      if (tabName && !tabsToShow.includes(tabName)) {
        tabsToHide.push(tabName);
      }
    }

    for (const tab of tabsToHide) {
      const sheetIndex = spread.getSheetIndex(tab);
      if (sheetIndex) {
        // console.log(`removeSheet`);
        spread.removeSheet(sheetIndex);
      }
    }
  }
}
